
import { useState, useEffect } from 'react';
import { Business } from '@/data/types/businessTypes';
import { BusinessFormData } from '../types';

export const useFormState = (business: Business | null) => {
  // Create initial empty form state
  const emptyFormData: BusinessFormData = {
    name: '',
    displayName: '',
    primaryPhone: '',
    primaryEmail: '',
    websiteUrl: '',
    openingDate: '',
    address: '',
    cityName: '',
    stateCode: '',
    postalCode: '',
    industry: '',
    timeZone: ''
  };
  
  // State for form fields
  const [formData, setFormData] = useState<BusinessFormData>(emptyFormData);
  
  // Track initial form data for comparison
  const [initialFormData, setInitialFormData] = useState<BusinessFormData>(emptyFormData);
  
  // Track if display name has been manually edited
  const [displayNameManuallyEdited, setDisplayNameManuallyEdited] = useState(false);
  
  // Initialize form with business data from context
  useEffect(() => {
    if (business) {
      // Map business data to our form fields, handling potential missing fields with fallbacks
      const newFormData = {
        name: business.name || '',
        displayName: business.displayName || business.shortName || '',
        primaryPhone: business.primaryPhone || business.phone || '',
        primaryEmail: business.primaryEmail || business.email || '',
        websiteUrl: business.websiteUrl || business.website || '',
        openingDate: business.openingDate || business.founded || '',
        address: business.address || '',
        cityName: business.cityName || business.city || '',
        stateCode: business.stateCode || business.state || '',
        postalCode: business.postalCode || business.zip || '',
        industry: business.industry || '',
        timeZone: business.timeZone || 'America/Los_Angeles'  // Default timezone if not set
      };
      
      setFormData(newFormData);
      setInitialFormData({...newFormData});
      
      // Reset the manually edited flag when opening the form
      setDisplayNameManuallyEdited(false);
    }
  }, [business]);
  
  // Update display name when business name changes (if not manually edited)
  useEffect(() => {
    if (!displayNameManuallyEdited && formData.name) {
      setFormData(prev => ({
        ...prev,
        displayName: formData.name.length > 30 
          ? formData.name.substring(0, 30) 
          : formData.name
      }));
    }
  }, [formData.name, displayNameManuallyEdited]);

  return {
    formData,
    initialFormData,
    setFormData,
    displayNameManuallyEdited,
    setDisplayNameManuallyEdited
  };
};
